import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useListAppointmentsQuery,
  type Appointment
} from '@/api/mcp-appointments';
import { AppointmentsCalendar, type CalendarVisibleRange } from '@/components/appointments';
import { ClientAppointmentDetailsPopup } from '@/components/appointments/client/ClientAppointmentDetailsPopup';
import { ClientAppointmentChangeRequestModal } from '@/components/appointments/client/ClientAppointmentChangeRequestModal';
import { normalizeDetailsReference } from '@/components/appointments/calendar/calendarUtils';
import { AutoRefreshToggle } from '@/components/ui/AutoRefreshToggle';
import PageTransition from '@/components/ui/PageTransition';
import { PageHeader } from '@/components/ui/PageHeader';
import { ClientScopeEmpty } from '@/components/client-portal/ClientScopeEmpty';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { useClientPortalScope } from '@/hooks/useClientPortalScope';
import { useFloatingPopup } from '@/hooks/useFloatingPopup';

export const Route = createFileRoute('/client/calendar')({
  component: ClientCalendarPage
});

function ClientCalendarPage() {
  const { t } = useTranslation('dashboard');
  const { isAdmin, adminScopeReady, selectedCustomerId, selectedClientId } = useClientPortalScope();
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [visibleRange, setVisibleRange] = useState<CalendarVisibleRange | null>(null);
  const [activeAppointment, setActiveAppointment] = useState<Appointment | null>(null);
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [changeRequestAppointment, setChangeRequestAppointment] = useState<Appointment | null>(null);

  const {
    open: detailsOpen,
    openAt: openDetailsAt,
    close: closeDetailsPopup,
    refs: detailsRefs,
    floatingStyles: detailsFloatingStyles,
    isPositioned: detailsPositioned
  } = useFloatingPopup();

  const rangeStart = (visibleRange?.start
    ? dayjs(visibleRange.start)
    : selectedDate.startOf('month').subtract(7, 'day')
  ).toISOString();
  const rangeEnd = (visibleRange?.end
    ? dayjs(visibleRange.end)
    : selectedDate.endOf('month').add(7, 'day')
  ).toISOString();

  const appointmentParams = useMemo(
    () => ({
      includeBookingDetails: true,
      startDate: rangeStart,
      endDate: rangeEnd,
      ...(isAdmin && selectedCustomerId ? { userId: selectedCustomerId } : {})
    }),
    [rangeStart, rangeEnd, isAdmin, selectedCustomerId]
  );

  const { data: appointments, isLoading, isFetching, dataUpdatedAt } = useListAppointmentsQuery(
    appointmentParams,
    adminScopeReady,
    autoRefresh ? AUTOREFRESH_INTERVAL : false
  );

  const lastUpdatedText = useLastUpdatedText(dataUpdatedAt);

  const appointmentList = useMemo(
    () => (Array.isArray(appointments) ? appointments : []),
    [appointments]
  );

  const closeDetails = useCallback(() => {
    closeDetailsPopup();
    setActiveAppointment(null);
  }, [closeDetailsPopup]);

  const openDetails = useCallback(
    (appointment: Appointment, el: HTMLElement) => {
      setActiveAppointment(appointment);
      openDetailsAt(normalizeDetailsReference(el));
    },
    [openDetailsAt]
  );

  if (isAdmin && !adminScopeReady) {
    return <ClientScopeEmpty description={t('client.scopeRequired.calendar')} />;
  }

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<CalendarOutlined />}
        title={t('navigation:client.myCalendar')}
        description={t('client.calendar.subtitle')}
        actions={
          <AutoRefreshToggle
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            isFetching={isFetching}
            lastUpdated={lastUpdatedText}
          />
        }
      />

      <AppointmentsCalendar
        appointments={appointmentList}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onAppointmentClick={() => undefined}
        onOpenDetails={openDetails}
        onVisibleRangeChange={setVisibleRange}
        isLoading={isLoading}
        audience="customer"
      />

      {activeAppointment ? (
        <ClientAppointmentDetailsPopup
          appointment={activeAppointment}
          visible={detailsOpen}
          isPositioned={detailsPositioned}
          floatingStyles={detailsFloatingStyles}
          floatingRef={detailsRefs.setFloating}
          onClose={closeDetails}
          onRequestChange={(appointment) => {
            closeDetails();
            setChangeRequestAppointment(appointment);
            setChangeRequestOpen(true);
          }}
        />
      ) : null}

      <ClientAppointmentChangeRequestModal
        appointment={changeRequestAppointment}
        open={changeRequestOpen}
        onClose={() => {
          setChangeRequestOpen(false);
          setChangeRequestAppointment(null);
        }}
      />
    </PageTransition>
  );
}
